import { useEffect, useRef, useState } from 'react';
import { useGetMyCompletedTrades } from 'api/queries/trades/get-my-completed-trades';
import { useModals } from 'providers/Modals';
import { useAppState } from 'providers/AppState';

const CHECK_INTERVAL = 15000; // ms

/**
 * Polls for newly completed trades and shows a notification modal to the seller.
 * Follows the same pattern as `useWatchMyApprovedTrades`: on first load it records
 * all existing completed trade IDs, then on subsequent polls detects new ones.
 */
export default function useWatchMyCompletedTrades() {
  const { open, openedModal } = useModals();
  const [trigger, setTrigger] = useState(false);

  const { blockRequests } = useAppState();

  const seenTradeIds = useRef<Set<string>>(new Set());
  const initialized = useRef(false);

  // pause polling while the notification modal is open
  const { data, refetch } = useGetMyCompletedTrades(openedModal !== 'trade-completed-notification');

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    if (!data || blockRequests) {
      return () => {
        if (timeout !== null) clearTimeout(timeout);
      };
    }

    refetch()
      .then(({ data: result }) => {
        if (!result) return;

        // seed the seen set on first load so existing trades do not trigger a notification
        if (!initialized.current) {
          initialized.current = true;
          result.forEach((item) => {
            seenTradeIds.current.add(item.data.id);
          });
          return;
        }

        // detect new completed trade
        const newTrade = result.find((item) => !seenTradeIds.current.has(item.data.id));
        if (newTrade) {
          seenTradeIds.current.add(newTrade.data.id);
          open('trade-completed-notification', { tradeId: newTrade.data.id, tradeCode: newTrade.data.code });
        }
      })
      .catch(console.error)
      .finally(() => {
        timeout = setTimeout(() => {
          setTrigger((prev) => !prev);
        }, CHECK_INTERVAL);
      });

    return () => {
      if (timeout !== null) clearTimeout(timeout);
    };
  }, [data, trigger, blockRequests, refetch, open]);
}
